/**
 * Hybrid logical clocks — the ordering primitive the whole sync protocol rests on.
 *
 * See docs/design/03-sync-protocol.md §2. A plain wall-clock timestamp is not
 * enough for two reasons: two devices a minute apart would let the one that is
 * behind permanently lose every conflict, and a device whose clock jumps
 * backwards would invert causality. An HLC keeps wall-clock meaning while
 * guaranteeing monotonicity.
 *
 * `deviceId` is the final tie-breaker, which makes the ordering **total** rather
 * than partial. That is what makes FR-10.6's "deterministically" literally true:
 * the same set of events folds to the same result regardless of arrival order or
 * which device does the folding.
 *
 * Runs identically on client and server (ADR-0003). One copy, never two.
 */
/**
 * Encoding widths. Fixed, because lexicographic order must equal causal order --
 * that is what lets a DynamoDB range query on `SK` do the ordering for free
 * (docs/design/02-data-model.md §2.2). A value that overflowed its field would
 * widen the string and silently break that equivalence, so `encode` refuses.
 */
const MILLIS_HEX_WIDTH = 12;
const COUNTER_HEX_WIDTH = 4;
/** 2^48 ms ≈ year 10889. */
const MILLIS_MAX = 2 ** (MILLIS_HEX_WIDTH * 4) - 1;
/** 65,536 events in a single millisecond on one device. */
const COUNTER_MAX = 2 ** (COUNTER_HEX_WIDTH * 4) - 1;
/** The clock a device starts with before it has issued or seen anything. */
export function origin(deviceId) {
    return { millis: 0, counter: 0, deviceId };
}
/**
 * The clock for a new local write.
 *
 * When the wall clock has advanced, it takes over and the counter resets. When it
 * has not -- including when it has gone *backwards* -- the stored millis is held
 * and the counter increments instead. That second branch is what makes a
 * corrected-backwards clock harmless (correctness case 6).
 */
export function now(local, wall) {
    const millis = Math.max(wall, local.millis);
    const counter = millis === local.millis ? local.counter + 1 : 0;
    return { millis, counter, deviceId: local.deviceId };
}
/**
 * How far ahead of local wall time a remote clock may be and still be adopted.
 *
 * A day, which is far wider than real clock skew between devices that both sync
 * and far narrower than the drift that causes the damage below. See ADR-0013.
 */
export const MAX_REMOTE_DRIFT_MS = 24 * 60 * 60 * 1000;
/**
 * True when a remote clock is too far ahead to be believable.
 *
 * Exported so a client can tell the user their device's clock looks wrong, which
 * is the only thing that actually fixes the underlying problem.
 */
export const isImplausible = (remote, wall, maxDriftMs = MAX_REMOTE_DRIFT_MS) => remote.millis > wall + maxDriftMs;
/**
 * The clock after observing a remote event.
 *
 * Takes the maximum of the three inputs, then increments whichever source that
 * maximum came from so the result strictly exceeds every clock seen. The returned
 * clock always carries the *local* deviceId: receiving an event does not make this
 * device the other one.
 *
 * **A remote clock beyond `maxDriftMs` is not adopted** (ADR-0013). Because a
 * hybrid logical clock only moves forward, one device whose wall clock is wrong by
 * years would otherwise drag every other device's clock with it permanently, and
 * every subsequent write anywhere would lose its merges until real time caught up.
 *
 * The event is still applied and still ordered by its own clock -- rejecting it
 * would strand data the writing device has already shown its user. The cost is
 * narrower and stated plainly: that one event keeps winning its own field until
 * wall time passes it. That is a bounded, visible loss of ordering, against an
 * unbounded one.
 */
export function receive(local, remote, wall, maxDriftMs = MAX_REMOTE_DRIFT_MS) {
    if (isImplausible(remote, wall, maxDriftMs)) {
        // Advance as for a local write: forward, but not to where the outlier claims.
        return now(local, wall);
    }
    const millis = Math.max(wall, local.millis, remote.millis);
    const counter = millis === local.millis && millis === remote.millis
        ? Math.max(local.counter, remote.counter) + 1
        : millis === local.millis
            ? local.counter + 1
            : millis === remote.millis
                ? remote.counter + 1
                : 0;
    return { millis, counter, deviceId: local.deviceId };
}
/**
 * Serializes to `<millis>-<counter>-<deviceId>`, zero-padded lowercase hex, so
 * that string comparison is causal comparison.
 */
export function encode(h) {
    if (!Number.isInteger(h.millis) || h.millis < 0 || h.millis > MILLIS_MAX) {
        throw new RangeError(`Hlc millis out of range: ${h.millis}`);
    }
    if (!Number.isInteger(h.counter) || h.counter < 0 || h.counter > COUNTER_MAX) {
        // Reachable only by 65,536 writes inside one millisecond on one device. Throwing
        // beats truncating: a widened field would corrupt the sort order of every later
        // event, and a corrupted log is the one failure this design cannot recover from.
        throw new RangeError(`Hlc counter out of range: ${h.counter}`);
    }
    // Hyphens in a deviceId are permitted -- `decode` splits on the first two
    // delimiters only, so `dev-a1` round-trips.
    if (h.deviceId === '' || /\s/.test(h.deviceId)) {
        throw new RangeError(`Hlc deviceId must be non-empty and whitespace-free: "${h.deviceId}"`);
    }
    const millis = h.millis.toString(16).padStart(MILLIS_HEX_WIDTH, '0');
    const counter = h.counter.toString(16).padStart(COUNTER_HEX_WIDTH, '0');
    return `${millis}-${counter}-${h.deviceId}`;
}
/**
 * Parses an encoded clock.
 *
 * Splits on the first two delimiters only, because a deviceId may itself contain
 * hyphens -- the protocol document's own examples are `dev-a1` and `srv`. A naive
 * `split('-')` would mangle them.
 */
export function decode(encoded) {
    const first = encoded.indexOf('-');
    const second = encoded.indexOf('-', first + 1);
    if (first !== MILLIS_HEX_WIDTH || second !== first + 1 + COUNTER_HEX_WIDTH) {
        throw new SyntaxError(`Malformed Hlc: "${encoded}"`);
    }
    const millisHex = encoded.slice(0, first);
    const counterHex = encoded.slice(first + 1, second);
    const deviceId = encoded.slice(second + 1);
    if (!/^[0-9a-f]+$/.test(millisHex) || !/^[0-9a-f]+$/.test(counterHex) || deviceId === '') {
        throw new SyntaxError(`Malformed Hlc: "${encoded}"`);
    }
    return {
        millis: parseInt(millisHex, 16),
        counter: parseInt(counterHex, 16),
        deviceId,
    };
}
/**
 * Total order over clocks: negative, zero, or positive, like a sort comparator.
 *
 * Agrees with lexicographic comparison of `encode`, which is what makes the
 * server's DynamoDB range reads and the client's in-memory folding produce the
 * same sequence.
 */
export function compare(a, b) {
    if (a.millis !== b.millis)
        return a.millis < b.millis ? -1 : 1;
    if (a.counter !== b.counter)
        return a.counter < b.counter ? -1 : 1;
    if (a.deviceId === b.deviceId)
        return 0;
    return a.deviceId < b.deviceId ? -1 : 1;
}
/** True when `a` happened after `b` in the total order. Reads better at call sites than `compare(...) > 0`. */
export function isAfter(a, b) {
    return compare(a, b) > 0;
}
//# sourceMappingURL=hlc.js.map